import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, RefreshCw, CheckCircle2, AlertTriangle, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

const CAT_TONE = {
  MATCHED: "bg-emerald-100 text-emerald-700", MISSING_PAWKT_RECORD: "bg-rose-100 text-rose-700",
  MISSING_PROVIDER_RECORD: "bg-amber-100 text-amber-700", AMOUNT_MISMATCH: "bg-rose-100 text-rose-700",
  STATUS_MISMATCH: "bg-amber-100 text-amber-700", DUPLICATE: "bg-rose-100 text-rose-700",
  REFUND_MISMATCH: "bg-rose-100 text-rose-700", NEEDS_REVIEW: "bg-indigo-100 text-indigo-700",
};

export default function PaymentOps() {
  const qc = useQueryClient();
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("reconciled_manually");
  const [reason, setReason] = useState("");

  const metrics = useQuery({ queryKey: ["pay-metrics"], queryFn: async () => (await api.get("/payments/metrics")).data });
  const report = useQuery({ queryKey: ["pay-recon"], queryFn: async () => (await api.get("/payments/reconciliation/report")).data });
  const reviews = useQuery({ queryKey: ["pay-reviews"], queryFn: async () => (await api.get("/payments/needs-review")).data });

  const scan = async () => {
    try {
      await api.get("/payments/reconciliation/report?record=true");
      qc.invalidateQueries({ queryKey: ["pay-recon"] });
      qc.invalidateQueries({ queryKey: ["pay-reviews"] });
      qc.invalidateQueries({ queryKey: ["pay-metrics"] });
      toast.success("Reconciliation scan complete");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitResolve = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    try {
      await api.post(`/payments/needs-review/${resolving.id}/resolve`, { resolution, reason });
      toast.success("Marked resolved");
      setResolving(null); setReason("");
      qc.invalidateQueries({ queryKey: ["pay-reviews"] });
      qc.invalidateQueries({ queryKey: ["pay-metrics"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const g = metrics.data?.gateway?.razorpay;
  const m = metrics.data || {};
  const summary = report.data?.summary || {};

  return (
    <div>
      <PageHeader title="Payment Ops" subtitle="Razorpay status, reconciliation & needs-review — detect, report, resolve">
        <Button onClick={scan} className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="recon-scan">
          <RefreshCw className="h-4 w-4" /> Run reconciliation scan
        </Button>
      </PageHeader>

      {/* Gateway + metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="gateway-status">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            {g?.connected ? <Wifi className="h-4 w-4 text-emerald-600" /> : <WifiOff className="h-4 w-4 text-slate-400" />} Razorpay
          </div>
          <div className={`mt-1 font-display text-lg font-bold ${g?.connected ? "text-emerald-600" : "text-slate-500"}`}>
            {g?.connected ? "Connected" : "Not Connected"}
          </div>
          <div className="text-xs text-slate-400">{g?.connected ? (g?.webhook_ready ? "Webhook ready" : "Webhook secret missing") : "Sandbox / manual mode"}</div>
        </div>
        <Metric label="Orders created" value={m.orders_created} testid="m-orders" />
        <Metric label="Captured" value={m.payments_captured} testid="m-captured" />
        <Metric label="Needs review" value={m.needs_review_open} tone="text-indigo-600" testid="m-review" />
        <Metric label="Refunds" value={m.refunds} />
        <Metric label="Webhooks received" value={m.webhook?.received} />
        <Metric label="Verify failures" value={m.webhook?.verification_failures} tone="text-rose-600" />
        <Metric label="Duplicate webhooks" value={m.webhook?.duplicates} />
      </div>

      {/* Reconciliation summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4 text-slate-400" /><h3 className="font-display font-semibold">Reconciliation</h3>
          {report.data?.clean ? <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="h-3 w-3" /> Clean</Badge>
            : <Badge className="bg-rose-100 text-rose-700 gap-1"><AlertTriangle className="h-3 w-3" /> Discrepancies</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary).map(([k, v]) => (
            <Badge key={k} className={`${CAT_TONE[k] || "bg-slate-100 text-slate-600"}`} data-testid={`recon-${k}`}>{k.replace(/_/g, " ")}: {v}</Badge>
          ))}
        </div>
      </div>

      {/* Needs review */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="font-display font-semibold mb-3">Needs review <span className="text-slate-400">({reviews.data?.count || 0})</span></h3>
        {(reviews.data?.items || []).length === 0 && <p className="text-sm text-slate-400 py-4">Nothing to review — payments reconcile cleanly.</p>}
        <div className="divide-y divide-slate-100">
          {(reviews.data?.items || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2.5" data-testid={`review-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge className="bg-indigo-100 text-indigo-700 uppercase text-[10px]">{r.kind?.replace(/_/g, " ")}</Badge>
                  <span className="text-sm text-slate-700 truncate">{r.reason}</span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  inv {r.detail?.invoice_id || "—"} · order {r.detail?.order_id || "—"} · ₹{r.detail?.amount ?? "—"}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => { setResolving(r); setReason(""); }} data-testid={`review-resolve-${r.id}`}>Resolve</Button>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-400">Resolution is non-destructive — money only changes via the payment state machine (verify / webhook / refund). There is no force-paid.</p>
      </div>

      <Dialog open={!!resolving} onOpenChange={(o) => !o && setResolving(null)}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle>Resolve review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">{resolving?.reason}</div>
            <div>
              <Label className="text-xs">Resolution</Label>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger className="bg-white" data-testid="resolve-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="reconciled_manually">Reconciled manually</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="dismissed">Dismissed (not a real issue)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason (recorded in audit)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} className="bg-white" data-testid="resolve-reason" />
            </div>
            <Button onClick={submitResolve} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="resolve-submit">Confirm resolution</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value, tone, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-sm text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tone || "text-slate-900"}`}>{value ?? 0}</div>
    </div>
  );
}
